import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { messageService } from '../utils/messaging';
import clientApi from '../utils/apiUtils';
import messageApi from '../utils/api/messageApi';
import { NOTIFICATION_TYPES } from '../utils/notifications/notificationTypes';
import { isViewingMessage, messageNotificationUrl } from '../utils/notifications/messageNotification';
import notificationsApi from '../utils/api/notificationsApi';
import { getAuthHeaders } from '../utils/api/common';
import config from '../config';

const MessageContext = createContext();

// Create a notification callback ref that can be set by components
let notificationCallback = null;

export function setNotificationCallback(callback) {
  notificationCallback = callback;
}

export function MessageProvider({ children, enabled = true }) {
  const { user: sessionUser, isAuthenticated } = useAuth();
  const user = enabled && isAuthenticated ? sessionUser : null;
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [unreadCount, setUnreadCount] = useState(0);
  const [orgUnreadCount, setOrgUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [currentUserDetails, setCurrentUserDetails] = useState(null);
  // Timestamp of the last successful messagesLoaded event. Used by the
  // messages page to render "Last refreshed Xm ago" so users have a visible
  // signal that the empty list is current and not just a stale render.
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  
  // Track the currently visible conversation for notification logic
  const visibleConversationRef = useRef(null);
  const notificationPreferences = useRef(null);
  useEffect(() => {
    let active = true;
    notificationPreferences.current = null;
    if (!user?.sub) return undefined;
    const organizationId = user.organizationId || 0;
    notificationsApi.getPreferences(organizationId).then((response) => {
      if (active && response.adopted) notificationPreferences.current = response.preferences || [];
    }).catch(() => {});
    const update = (event) => {
      if (String(event.detail?.organizationId) === String(organizationId)) notificationPreferences.current = event.detail.preferences;
    };
    window.addEventListener('sena:notification-preferences-saved', update);
    return () => { active = false; window.removeEventListener('sena:notification-preferences-saved', update); };
  }, [user?.sub, user?.organizationId, user?.contextReferenceId]);

  // Determine the current user's messaging identifier consistently.
  // Store in a ref so that event handler closures always read the latest value
  // without needing to re-subscribe to the event emitter.
  const currentUserId = currentUserDetails?.referenceId || user?.sub;
  const currentUserIdStr = currentUserId != null ? String(currentUserId) : '';
  const currentUserIdStrRef = useRef(currentUserIdStr);
  useEffect(() => {
    currentUserIdStrRef.current = currentUserIdStr;
  }, [currentUserIdStr]);

  const isUnreadForCurrentUser = useCallback((message) => {
    const currentId = currentUserIdStrRef.current;
    if (!currentId || !message || message.read) return false;
    if (String(message.senderId ?? '') === currentId) return false;

    const recipientId = message.recipientId;
    return recipientId == null || recipientId === '' || String(recipientId) === currentId;
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
    if (userRole !== 'client') {
      setUnreadCount(0);
      return;
    }

    try {
      const count = await messageApi.fetchUnreadCount();
      const normalizedCount = Number.isFinite(Number(count)) ? Number(count) : 0;
      setUnreadCount(normalizedCount);
    } catch {
      // Keep the locally-derived count when the count endpoint is unavailable.
    }
  }, []);

  const refreshPracticeInboxUnreadCount = useCallback(async () => {
    const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
    if (!user || userRole === 'client') return;

    try {
      const result = await messageApi.fetchPracticeInboxUnreadCount();
      setOrgUnreadCount(Number(result?.unreadMessageCount || 0));
    } catch {
      // Polling remains the fallback when the request or SignalR connection fails.
    }
  }, [user]);



  // Track message IDs that have been explicitly marked as read locally so that
  // a SignalR reconnect + messagesLoaded cannot resurrect stale unread state.
  // The backend unread-count endpoint and the per-message `read` field may be
  // separate data paths; when the hub reloads messages they can still carry
  // read:false even though the count API returns 0.
  const localReadIdsRef = useRef(new Set());

  useEffect(() => {
    setMessages([]);
    setTypingUsers(new Set());
    setOnlineUsers(new Set());
    setUnreadCount(0);
    setOrgUnreadCount(0);
    setIsConnected(false);
    setCurrentUserDetails(null);
    setLastFetchedAt(null);
    visibleConversationRef.current = null;
    localReadIdsRef.current = new Set();
    messageService.disconnect();
  }, [user?.sub, user?.contextType, user?.contextReferenceId]);

  // Fetch current user details for referenceId
  useEffect(() => {
    let cancelled = false;

    const fetchCurrentUserDetails = async () => {
      try {
        const userDetails = await clientApi.fetchCurrentUser();
        if (cancelled) return;
        setCurrentUserDetails(userDetails);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching current user details in MessageContext:', err);
        setCurrentUserDetails(null);
      }
    };

    if (user) {
      fetchCurrentUserDetails();
    } else {
      setCurrentUserDetails(null);
    }

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    let intervalId = null;

    const fetchCount = async () => {
      if (cancelled) return;
      await refreshUnreadCount();
    };

    fetchCount();
    intervalId = setInterval(fetchCount, 30000);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [user, refreshUnreadCount]);

  // Fetch org unread count independently so the sidebar badge is accurate on
  // any page, not just when MidwifeMessagesPage is mounted.
  // Only for midwife/admin users — clients should not poll org conversations.
  useEffect(() => {
    const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
    if (!user || userRole === 'client') return;

    let cancelled = false;
    let intervalId = null;

    const start = async () => {
      let orgId =
        currentUserDetails?.organizationId || currentUserDetails?.organization?.id;

      if (!orgId) {
        try {
          const org = await clientApi.getMyOrganization();
          orgId = org?.id;
        } catch {
          // no org found
        }
      }

      if (!orgId || cancelled) return;

      await refreshPracticeInboxUnreadCount();
      if (!cancelled)
        intervalId = setInterval(refreshPracticeInboxUnreadCount, 30000);
    };

    start();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [user, currentUserDetails, refreshPracticeInboxUnreadCount]);

  useEffect(() => {
    if (!user || !currentUserDetails) return;

    if (currentUserDetails.__localSubscriptionBypass) {
      setIsConnected(false);
      return;
    }

    let retryCount = 0;
    const maxRetries = 3;
    let retryTimeout;
    let cancelled = false;

    // Initialize SignalR connection
    const initializeMessaging = async () => {
      if (cancelled) return;
      try {
        const token = localStorage.getItem('velaToken');
        // Use referenceId if available, otherwise fall back to internal ID
        const usedUserId = currentUserDetails?.referenceId || user.sub;
        
        const success = await messageService.initialize(usedUserId, token);
        if (cancelled) return;
        
        if (!success && retryCount < maxRetries) {
          retryCount++;
          retryTimeout = setTimeout(initializeMessaging, 5000);
        }
      } catch (error) {
        if (cancelled) return;
        if (retryCount < maxRetries) {
          retryCount++;
          retryTimeout = setTimeout(initializeMessaging, 5000);
        }
      }
    };

    initializeMessaging();

    // Set up event listeners
    messageService.on('messageReceived', handleMessageReceived);
    messageService.on('userTyping', handleUserTyping);
    messageService.on('userStatusChanged', handleUserStatusChanged);
    messageService.on('connectionStateChanged', (state) => {
      setIsConnected(state === 'connected');
    });
    // Hub pushes this when the recipient marks our outgoing messages as read.
    messageService.on('messagesRead', (readIds) => {
      if (!Array.isArray(readIds) || readIds.length === 0) return;
      const readIdSet = new Set(readIds.map(String));
      setMessages(prev => {
        const updated = prev.map(m =>
          readIdSet.has(String(m.id)) ? { ...m, read: true } : m
        );
        // unreadCount is for received messages so this push doesn't change it,
        // but keep the state object stable when nothing actually changed.
        return updated.some((m, i) => m !== prev[i]) ? updated : prev;
      });
    });
    messageService.on('practiceInboxChanged', refreshPracticeInboxUnreadCount);
    messageService.on('messagesLoaded', (loadedMessages) => {
      // Sort messages by timestamp (oldest first to match display)
      const sortedMessages = loadedMessages
        .map(m => ({
          ...m,
          // Ensure UTC timestamp
          timestamp: m.timestamp.endsWith('Z') ? 
            new Date(m.timestamp).toISOString() : 
            new Date(m.timestamp + 'Z').toISOString(),
          // Re-apply any locally-acknowledged read marks so that a SignalR
          // reconnect cannot resurrect badges for messages the user already
          // dismissed.  The backend count endpoint and per-message `read`
          // field can be out of sync (separate data paths).
          read: m.read || localReadIdsRef.current.has(String(m.id)),
        }))
        .sort((a, b) => {
          const timeA = new Date(a.timestamp).getTime();
          const timeB = new Date(b.timestamp).getTime();
          return timeA - timeB;
        });
      
      setMessages(sortedMessages);
      setLastFetchedAt(Date.now());
      refreshUnreadCount();
    });

    // Reconnect when the app returns to the foreground (e.g. mobile Safari
    // drops the WebSocket while backgrounded).
    const handleVisibilityChange = async () => {
      if (!cancelled && document.visibilityState === 'visible') {
        const state = messageService.getConnectionState();
        if (state !== 'connected' && state !== 'reconnecting') {
          const token = localStorage.getItem('velaToken');
          if (token) {
            const usedUserId = currentUserDetails?.referenceId || user.sub;
            await messageService.initialize(usedUserId, token);
          }
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      messageService.off('messageReceived', handleMessageReceived);
      messageService.off('userTyping', handleUserTyping);
      messageService.off('userStatusChanged', handleUserStatusChanged);
      messageService.off('connectionStateChanged');
      messageService.off('messagesLoaded');
      messageService.off('messagesRead');
      messageService.off('practiceInboxChanged', refreshPracticeInboxUnreadCount);
      messageService.disconnect();
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [user, currentUserDetails, refreshUnreadCount, refreshPracticeInboxUnreadCount]);

  const handleMessageReceived = (message) => {
    if (!message) return;

    if (user?.organizationReferenceId &&
        message.organizationId &&
        message.organizationId !== user.organizationReferenceId) return;

    // Org messages (channel or DM within the org tab) carry a conversationId.
    // They are managed separately in MidwifeMessagesPage and must not be added
    // to the client messages state or counted in the client unreadCount.
    if (message.conversationId) return;
    
    // Check if this is a message from another user (not from current user)
    const isForCurrentUser =
      message.recipientId == null ||
      message.recipientId === '' ||
      String(message.recipientId) === currentUserIdStrRef.current;
    const isFromOtherUser =
      isForCurrentUser && String(message.senderId) !== currentUserIdStrRef.current;
    
    // Check if user is currently viewing this conversation
    const isViewingConversation = isViewingMessage(visibleConversationRef.current, message);
    
    // Show in-app notification if:
    // 1. Message is from another user
    // 2. User is NOT viewing this conversation
    // 3. App has focus (user is using the app but on a different conversation/page)
    const messageEventKey = String(user?.role || localStorage.getItem('userRole')).toLowerCase() === 'client'
      ? 'messages.client_received' : 'messages.care_team';
    const eventEnabled = notificationPreferences.current?.find((preference) => preference.eventKey === messageEventKey)?.enabled !== false;
    if (eventEnabled && isFromOtherUser && !isViewingConversation && document.hasFocus() && notificationCallback) {
      // Fetch sender name asynchronously
      fetchSenderName(message.senderId).then((senderName) => {
        const notificationData = {
          type: message.conversationId ? NOTIFICATION_TYPES.ORG_MESSAGE : NOTIFICATION_TYPES.CLIENT_MESSAGE,
          title: senderName,
          body: message.content,
          timestamp: message.timestamp,
          navigateTo: messageNotificationUrl(message, user?.role || localStorage.getItem('userRole')),
          data: {
            messageId: message.id,
            senderId: message.senderId,
            conversationId: message.conversationId
          }
        };
        
        notificationCallback(notificationData);
      }).catch(err => {
        console.error('Error showing notification:', err);
      });
    }
    
    setMessages(prev => {
      // Check if message already exists - be more lenient with comparison
      const exists = prev.some(m => {
        // If we have IDs, use them for comparison
        if (m.id && message.id && m.id === message.id) {
          return true;
        }
        
        // Otherwise, check content and sender (less strict)
        if (String(m.senderId) === String(message.senderId) && 
            m.content === message.content &&
            Math.abs(new Date(m.timestamp).getTime() - new Date(message.timestamp).getTime()) < 5000) {
          return true;
        }
        
        return false;
      });
      
      if (exists) {
        refreshUnreadCount();
        return prev;
      }
      
      // Add new message and ensure UTC timestamp
      const newMessage = {
        ...message,
        timestamp: message.timestamp.endsWith('Z') ? 
          new Date(message.timestamp).toISOString() : 
          new Date(message.timestamp + 'Z').toISOString()
      };
      
      // Add new message and sort by timestamp
      const newMessages = [...prev, newMessage].sort((a, b) => {
        const timeA = new Date(a.timestamp).getTime();
        const timeB = new Date(b.timestamp).getTime();
        return timeA - timeB;
      });
      refreshUnreadCount();
      return newMessages;
    });
  };

  // Helper function to fetch sender name
  const fetchSenderName = async (senderId) => {
    try {
      const response = await fetch(`${config.api.url}/user/${senderId}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const userData = await response.json();
        return `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'User';
      }
    } catch (error) {
      console.error('Error fetching sender name:', error);
    }
    return 'New Message';
  };

  const handleUserTyping = (userId) => {
    setTypingUsers(prev => new Set(prev).add(userId));
    // Clear typing indicator after 3 seconds
    setTimeout(() => {
      setTypingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }, 3000);
  };

  const handleUserStatusChanged = ({ userId, isOnline }) => {
    setOnlineUsers(prev => {
      const newSet = new Set(prev);
      if (isOnline) {
        newSet.add(userId);
      } else {
        newSet.delete(userId);
      }
      return newSet;
    });
  };

  const sendMessage = async (recipientId, content, attachmentIds = []) => {
    const success = await messageService.sendMessage(recipientId, content, attachmentIds);
    if (success) {
      // Message will be added through the messageReceived handler with server timestamp
    }
    return success;
  };

  const sendTypingIndicator = (recipientId) => {
    messageService.sendTypingIndicator(recipientId);
  };

  const markAsRead = async (messageIds) => {
    // Persist the read acknowledgement locally so messagesLoaded (SignalR
    // reconnect) cannot resurrect the badge for these messages.
    messageIds.forEach(id => localReadIdsRef.current.add(String(id)));

    // Update messages to mark them as read immediately for UI responsiveness
    setMessages(prev => {
      const updated = prev.map(message => 
        messageIds.includes(message.id) ? { ...message, read: true } : message
      );
      // Recalculate unread count after marking as read
      const currentUnread = updated.filter(isUnreadForCurrentUser).length;
      setUnreadCount(currentUnread);
      return updated;
    });
    
    // Persist read status to backend
    try {
      await messageApi.markMessagesAsRead(messageIds);
      await refreshUnreadCount();
    } catch (error) {
      console.error('Error persisting read status:', error);
      // Optionally revert the local changes if backend update fails
      // For now, we'll keep the local changes for better UX
    }
  };

  const clearMessages = () => {
    setMessages([]);
    setUnreadCount(0);
  };

  // Manual refetch of the message history. Used by the empty-state "Refresh
  // messages" button so users can self-recover when the list looks empty —
  // e.g. if a stale bundle's filter dropped data, or SignalR missed a push.
  const refreshMessages = useCallback(async () => {
    try {
      await messageService.fetchMessageHistory();
      return true;
    } catch (err) {
      console.error('Error refreshing messages:', err);
      return false;
    }
  }, []);

  // Function to set the currently visible conversation
  const setVisibleConversation = (conversationInfo) => {
    visibleConversationRef.current = conversationInfo;
  };

  const value = {
    messages,
    typingUsers,
    onlineUsers,
    unreadCount,
    orgUnreadCount,
    refreshPracticeInboxUnreadCount,
    sendMessage,
    sendTypingIndicator,
    markAsRead,
    clearMessages,
    setMessages,
    isConnected,
    setVisibleConversation,
    lastFetchedAt,
    refreshMessages,
  };

  return (
    <MessageContext.Provider value={value}>
      {children}
    </MessageContext.Provider>
  );
}

export function useMessages() {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error('useMessages must be used within a MessageProvider');
  }
  return context;
} 
